import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import ImageUpload from "../../components/ImageUpload";
import { useSiteContent } from "../../context/SiteContentContext";

// Every image key used across the storefront.
const MEDIA_KEYS = [
  { key: "home_hero", label: "Home · Hero image" },
  { key: "home_manifesto_ch1", label: "Home · Manifesto · Chapter 1 image" },
  { key: "home_manifesto_ch2", label: "Home · Manifesto · Chapter 2 image" },
  { key: "home_manifesto_ch3", label: "Home · Manifesto · Chapter 3 image" },
  { key: "about_hero", label: "About · Hero image" },
  { key: "about_atelier", label: "About · Atelier image" },
  { key: "about_detail", label: "About · Detail image" },
  { key: "journal_hero", label: "Journal · Hero image" },
  { key: "auth_hero", label: "Login / Sign-up · Side image" },
];

// Every text/copy key, grouped by page. Each entry ships with the CURRENT default
// so the admin can see what's live on the site, edit it inline, or reset it.
const TEXT_GROUPS = [
  {
    title: "Sections · Enable / disable",
    keys: [
      { key: "section_new_arrivals", label: "New Arrivals section (Just In)", default: "on", toggle: true },
      { key: "section_manifesto", label: "The Manifesto section", default: "on", toggle: true },
      { key: "home_manifesto_count", label: "Manifesto · Chapters to show (1 / 2 / 3)", default: "3" },
      { key: "section_bestsellers", label: "Best Sellers section", default: "on", toggle: true },
      { key: "section_testimonials", label: "Words from our muses (testimonials)", default: "on", toggle: true },
      { key: "section_instagram", label: "Follow the atelier (Instagram)", default: "on", toggle: true },
      { key: "section_occasions", label: "Shop by Occasion section", default: "on", toggle: true },
      { key: "section_collections", label: "Collections section", default: "on", toggle: true },
      { key: "section_journal", label: "Journal page (nav link + page)", default: "on", toggle: true },
    ],
  },
  {
    title: "Home · Hero",
    keys: [
      { key: "home_hero_kicker", label: "Kicker line", default: "The Wedding Edit · Autumn 2025" },
      { key: "home_hero_title_1", label: "Title · Line 1", default: "Heritage," },
      { key: "home_hero_title_2", label: "Title · Line 2 (italic)", default: "reimagined" },
      { key: "home_hero_title_3", label: "Title · Line 3", default: "for the modern muse." },
      { key: "home_hero_cta_1", label: "CTA · Primary", default: "Shop Now" },
      { key: "home_hero_cta_2", label: "CTA · Secondary", default: "Explore Collection" },
    ],
  },
  {
    title: "Home · Sections",
    keys: [
      { key: "home_collections_kicker", label: "Collections · Kicker", default: "01 — Collections" },
      { key: "home_collections_title_1", label: "Collections · Title 1", default: "The Curated" },
      { key: "home_collections_title_2", label: "Collections · Title 2 (italic)", default: "Wardrobe" },
      { key: "home_occasions_kicker", label: "Occasions · Kicker", default: "02 — Shop by Occasion" },
      { key: "home_occasions_title", label: "Occasions · Title", default: "Dress the moment" },
      { key: "home_new_kicker", label: "New Arrivals · Kicker", default: "03 — Just In" },
      { key: "home_new_title", label: "New Arrivals · Title", default: "New Arrivals" },
      { key: "home_manifesto_kicker", label: "Manifesto · Kicker", default: "04 — The Manifesto" },
      { key: "home_manifesto_title_1", label: "Manifesto · Title 1", default: "A house built" },
      { key: "home_manifesto_title_2", label: "Manifesto · Title 2 (italic)", default: "on patience" },
      { key: "home_bestsellers_kicker", label: "Best Sellers · Kicker", default: "05 — Most Coveted" },
      { key: "home_bestsellers_title", label: "Best Sellers · Title", default: "Best Sellers" },
      { key: "home_testimonials_header", label: "Testimonials · Header", default: "Words from our muses" },
      { key: "home_instagram_handle", label: "Instagram · Handle", default: "@amraiya" },
      { key: "home_instagram_title", label: "Instagram · Title", default: "Follow the atelier" },
    ],
  },
  {
    title: "Home · Manifesto chapters",
    keys: [
      { key: "home_manifesto_ch1_title", label: "Chapter 1 · Title", default: "The Karigar's hand" },
      { key: "home_manifesto_ch1_body", label: "Chapter 1 · Body", multiline: true,
        default: "Every thread is placed by artisans whose craft is inherited across generations. Weeks of zardozi, aari and pitta work live inside a single garment." },
      { key: "home_manifesto_ch2_title", label: "Chapter 2 · Title", default: "Cloth with a memory" },
      { key: "home_manifesto_ch2_body", label: "Chapter 2 · Body", multiline: true,
        default: "We source raw silk, organza and Banarasi weaves from the looms that first perfected them — fabrics that hold light, movement and history." },
      { key: "home_manifesto_ch3_title", label: "Chapter 3 · Title", default: "Made only for you" },
      { key: "home_manifesto_ch3_body", label: "Chapter 3 · Body", multiline: true,
        default: "Nothing is mass produced. Each piece is cut and tailored to order, so that what you wear exists, quite literally, for no one else." },
    ],
  },
  {
    title: "About page",
    keys: [
      { key: "about_kicker", label: "Kicker", default: "Our Story" },
      { key: "about_title_1", label: "Title · Line 1", default: "The house of" },
      { key: "about_title_2", label: "Title · Line 2 (italic)", default: "AMRAIYA" },
      { key: "about_lead", label: "Lead paragraph", multiline: true,
        default: "Amraiya — a warm glow of amber light — is where our garments live. Not quite tradition, not quite modernity, but the luminous space between." },
      { key: "about_body", label: "Body copy", multiline: true,
        default: "Founded on a reverence for Indian craft, Amraiya works with karigars across Banaras, Lucknow and Jaipur to create pieces that are made slowly, deliberately, and to last generations. Every silhouette is an heirloom in the making." },
      { key: "about_tile1", label: "Tile 1 label", default: "The atelier" },
      { key: "about_tile2", label: "Tile 2 label", default: "The detail" },
      { key: "about_stat1_num", label: "Stat 1 · Number", default: "12+" },
      { key: "about_stat1_label", label: "Stat 1 · Label", default: "Years of craft" },
      { key: "about_stat2_num", label: "Stat 2 · Number", default: "40+" },
      { key: "about_stat2_label", label: "Stat 2 · Label", default: "Master karigars" },
      { key: "about_stat3_num", label: "Stat 3 · Number", default: "100%" },
      { key: "about_stat3_label", label: "Stat 3 · Label", default: "Made to order" },
      { key: "about_cta", label: "CTA button", default: "Discover the collection" },
    ],
  },
  {
    title: "Contact page",
    keys: [
      { key: "contact_kicker", label: "Kicker", default: "Get in touch" },
      { key: "contact_title", label: "Title", default: "Contact us" },
      { key: "contact_intro", label: "Intro paragraph", multiline: true,
        default: "For bespoke enquiries, styling appointments or press, our client care team is at your service." },
      { key: "contact_addr_label", label: "Address · Label", default: "Flagship Atelier" },
      { key: "contact_addr_value", label: "Address · Value", default: "14 Kala Ghoda, Fort, Mumbai 400001" },
      { key: "contact_email_label", label: "Email · Label", default: "Email" },
      { key: "contact_email_value", label: "Email · Value", default: "care@amraiya.com" },
      { key: "contact_phone_label", label: "Phone · Label", default: "Telephone" },
      { key: "contact_phone_value", label: "Phone · Value", default: "+91 22 4000 1200" },
      { key: "contact_whatsapp_label", label: "WhatsApp · Label", default: "WhatsApp" },
      { key: "contact_whatsapp_value", label: "WhatsApp · Value", default: "Chat with a stylist, 10am–8pm IST" },
      { key: "contact_whatsapp_cta", label: "WhatsApp · Button text", default: "Chat on WhatsApp" },
      { key: "contact_form_title", label: "Form · Heading", default: "Send a message" },
    ],
  },
  {
    title: "Journal page",
    keys: [
      { key: "journal_kicker", label: "Kicker", default: "The Journal" },
      { key: "journal_title", label: "Title", default: "Stories & craft" },
    ],
  },
  {
    title: "Footer",
    keys: [
      { key: "footer_newsletter_title", label: "Newsletter · Heading", multiline: true,
        default: "Join the\natelier list" },
      { key: "footer_newsletter_body", label: "Newsletter · Body", multiline: true,
        default: "Private previews, restocks and invitations to our seasonal showings." },
      { key: "footer_shop_heading", label: "Column · Shop heading", default: "Shop" },
      { key: "footer_house_heading", label: "Column · House heading", default: "House" },
      { key: "footer_tagline", label: "Legal tagline", default: "AMRAIYA ATELIER — Made in India" },
    ],
  },
];

// Flatten to a defaults dictionary for merging.
const DEFAULTS = TEXT_GROUPS.reduce((acc, g) => {
  g.keys.forEach((k) => { acc[k.key] = k.default; });
  return acc;
}, {});

export default function AdminContent() {
  const [media, setMedia] = useState({});
  const [text, setText] = useState({});
  const [saving, setSaving] = useState(false);
  const site = useSiteContent();

  useEffect(() => {
    api.get("/site-content").then((r) => {
      const saved = r.data.text || {};
      // Pre-fill every field with its default, then overlay saved overrides so
      // the admin can see live copy and edit it directly.
      const merged = { ...DEFAULTS };
      Object.entries(saved).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") merged[k] = v;
      });
      setText(merged);
      setMedia(r.data.media || {});
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", { media, text });
      // Push the fresh state into the shared SiteContentProvider so the
      // storefront (Home, Footer, Navbar, etc.) reflects changes immediately
      // without a full page reload.
      await site.refresh();
      toast.success("Site content saved. Storefront updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetOne = (key) => setText((t) => ({ ...t, [key]: DEFAULTS[key] || "" }));

  const renderField = ({ key, label, multiline, default: def, toggle }) => {
    const value = text[key] ?? "";
    const isModified = value !== def;

    // Toggle-switch input for on/off style keys — much friendlier than a text field.
    if (toggle) {
      const isOn = value !== "off";
      return (
        <label key={key} className="flex items-center justify-between gap-3 p-3 border border-line hover:border-ink-soft rounded cursor-pointer transition-colors">
          <div>
            <p className="text-sm">{label}</p>
            <p className="text-[0.6rem] mono text-ink-soft mt-0.5">{isOn ? "ON — visible on site" : "OFF — hidden"}</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isOn}
            onClick={() => setText({ ...text, [key]: isOn ? "off" : "on" })}
            data-testid={`content-toggle-${key}`}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${isOn ? "bg-ink" : "bg-line"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-ivory transition-transform ${isOn ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </button>
        </label>
      );
    }

    return (
      <label key={key} className="block">
        <div className="flex items-center justify-between mb-1.5">
          <span className="overline text-ink-soft">{label}</span>
          {isModified && (
            <button
              type="button"
              onClick={() => resetOne(key)}
              className="text-[0.6rem] mono text-ink-soft hover:text-ink flex items-center gap-1"
              title="Reset to default"
              data-testid={`content-reset-${key}`}
            >
              <RotateCcw size={10} /> reset
            </button>
          )}
        </div>
        {multiline ? (
          <Textarea
            rows={3}
            value={value}
            onChange={(e) => setText({ ...text, [key]: e.target.value })}
            data-testid={`content-text-${key}`}
          />
        ) : (
          <Input
            value={value}
            onChange={(e) => setText({ ...text, [key]: e.target.value })}
            data-testid={`content-text-${key}`}
          />
        )}
      </label>
    );
  };

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-ivory/90 backdrop-blur-sm py-4 z-10 -mx-4 px-4 border-b border-line">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Site Content</h1>
          <p className="text-ink-soft text-sm mt-1">
            Every field below shows the current live copy — edit inline, save, and the storefront updates instantly.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-ink flex items-center gap-2 px-5 py-3 overline disabled:opacity-50"
          data-testid="save-content-btn"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-10">
        <div>
          <h2 className="overline text-ink-soft mb-4">Images</h2>
          <div className="space-y-6">
            {MEDIA_KEYS.map(({ key, label }) => (
              <ImageUpload
                key={key}
                label={label}
                value={media[key] || ""}
                onChange={(url) => setMedia({ ...media, [key]: url })}
              />
            ))}
          </div>
        </div>

        <div className="space-y-10">
          {TEXT_GROUPS.map((group) => (
            <div key={group.title}>
              <h2 className="overline text-ink-soft mb-4 pb-2 border-b border-line">{group.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {group.keys.map(renderField)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="btn-ink flex items-center gap-2 px-5 py-3 overline disabled:opacity-50"
          data-testid="save-content-btn-bottom"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
